import * as styles from "./LocationDialog.css";

import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { type RefObject, useEffect, useState } from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@frontend/components/atoms/Dialog";
import {
  Field,
  FieldContent,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import { Fieldset } from "@frontend/components/atoms/Fieldset";
import { Icon } from "@frontend/components/atoms/Icon";
import { Input } from "@frontend/components/atoms/Input";
import { Switch } from "@frontend/components/atoms/Switch";
import { ContactField } from "@frontend/shared/contact-field";
import { Trans, useLingui } from "@lingui/react/macro";

import { useContactAddresses } from "../hooks/useContactAddresses";
import { useLocationForm } from "../hooks/useLocationForm";
import { hasValidAddress } from "../schemas/location";
import { locationApi } from "../services";
import {
  autoNameAtom,
  autoPostalCodeAtom,
  hideAtom,
  isVisibleAtom,
  newAddressToggleAtom,
  selectExistingLocationAddressAtom,
} from "../stores";
import type { CreatedLocation } from "../types";
import { AddressField } from "./AddressField";
import { AddressFields } from "./AddressFields";

export type LocationDialogProps = {
  onSubmit?: (location: CreatedLocation) => void;
  onCancel?: () => void;
  originRef?: RefObject<DOMRect | null>;
};

export function LocationDialog({
  onSubmit,
  onCancel,
  originRef,
}: LocationDialogProps) {
  const { t } = useLingui();

  const isVisible = useAtomValue(isVisibleAtom);
  const hide = useAtom(hideAtom)[1];

  const { locationForm, submit } = useLocationForm();

  const [contactId] = useAtom(locationForm.fields.contactId.atom);
  const [name, setName] = useAtom(locationForm.fields.name.atom);
  const [autoName, setAutoName] = useAtom(autoNameAtom);
  const [createNewAddress, setCreateNewAddress] = useAtom(newAddressToggleAtom);
  const [autoPostalCode, setAutoPostalCode] = useAtom(autoPostalCodeAtom);
  const selectExistingAddress = useSetAtom(selectExistingLocationAddressAtom);
  const newStreet = useAtomValue(
    locationForm.fields.createNewAddress.fields.street.atom,
  );
  const newCity = useAtomValue(
    locationForm.fields.createNewAddress.fields.city.atom,
  );

  const addresses = useContactAddresses(contactId);

  const [saving, setSaving] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Auto-derive the location name from the chosen/new address. Both modes
  // populate the same `createNewAddress` fields (picking an existing address
  // loads it there read-only), unless the user opted out via the "Auto name"
  // checkbox.
  const computedName = [newStreet, newCity].filter(Boolean).join(", ");

  const handleToggleCreateNewAddress = (checked: boolean) => {
    setCreateNewAddress(checked);

    if (checked) {
      selectExistingAddress(null);
    } else {
      const first = addresses[0];

      selectExistingAddress(first ? first.id : null);
    }
  };

  useEffect(() => {
    if (autoName) {
      setName(computedName);
    }
  }, [autoName, computedName, setName]);

  const handleCancel = () => {
    onCancel?.();
    hide();
  };

  const handleSave = async () => {
    const result = await submit();

    if (!result.success) {
      return;
    }

    const data = result.data;
    const contactId = data.contactId;

    const nextContactError =
      null == contactId ? "Please select a contact." : null;

    const nextAddressError = hasValidAddress(data, createNewAddress)
      ? null
      : "Select an existing address, or fill in street, postal code and city for a new one.";

    setContactError(nextContactError);
    setAddressError(nextAddressError);

    if (null == contactId || nextAddressError) {
      return;
    }

    setSaveError(null);
    setSaving(true);

    try {
      let addressId: number | undefined;

      const addressInput = {
        place: data.createNewAddress.place || undefined,
        street: data.createNewAddress.street,
        extendedAddress: data.createNewAddress.extendedAddress || undefined,
        postOfficeBox: data.createNewAddress.postOfficeBox || undefined,
        postalCode: data.createNewAddress.postalCode,
        city: data.createNewAddress.city,
        country: data.createNewAddress.country || undefined,
      };

      if (createNewAddress) {
        const address = await locationApi.createAddress({
          contactId,
          ...addressInput,
        });

        addressId = address.id;
      } else if (null != data.addressId) {
        // The fields may have been edited since the address was selected —
        // persist those edits instead of just referencing it unchanged.
        await locationApi.updateAddress(data.addressId, addressInput);

        addressId = data.addressId;
      }

      const location = await locationApi.createLocation({
        contactId,
        name: data.name || undefined,
        addressId,
      });

      onSubmit?.(location);
      hide();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : String(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={isVisible}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleCancel();
        }
      }}
      originRef={originRef}
    >
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle className={styles.titleRow}>
            <Icon name="Building2" className={styles.titleIcon} />
            <Trans>New location</Trans>
          </DialogTitle>
          <DialogClose />
        </DialogHeader>

        <div className={styles.body}>
          <Fieldset orientation="horizontal" variant="card" size="md">
            <Field>
              <FieldLabel>
                <Trans>Name</Trans>
              </FieldLabel>
              <FieldContent>
                <Input
                  value={name}
                  onValueChange={setName}
                  placeholder={t`Name`}
                  disabled={autoName}
                />
              </FieldContent>
            </Field>

            <Field>
              <FieldContent>
                <Switch
                  label={t`Auto-generate name`}
                  checked={autoName}
                  onCheckedChange={setAutoName}
                />
              </FieldContent>
            </Field>

            <ContactField
              field={locationForm.fields.contactId}
              disabled={Boolean(createNewAddress)}
              error={contactError}
            />

            <AddressField
              field={locationForm.fields.addressId}
              addresses={addresses}
              disabled={Boolean(createNewAddress)}
              error={addressError}
              onAddressSelected={selectExistingAddress}
            />

            <Field>
              <FieldContent>
                <Switch
                  label={t`Create a new address`}
                  checked={Boolean(createNewAddress)}
                  onCheckedChange={handleToggleCreateNewAddress}
                />
              </FieldContent>
            </Field>
          </Fieldset>

          {/* Only a new address is editable here: picking an existing one
              shows its values read-only. */}
          <AddressFields
            fields={locationForm.fields.createNewAddress.fields}
            disabled={!createNewAddress}
            autoPostalCode={autoPostalCode}
            autoPostalCodeToggle={
              <Switch
                label={t`Auto postal code`}
                checked={autoPostalCode}
                onCheckedChange={setAutoPostalCode}
                disabled={!createNewAddress}
              />
            }
          />

          {saveError && <p role="alert">{saveError}</p>}
        </div>

        <DialogFooter>
          <Button variant="neutral" onClick={handleCancel}>
            <Trans>Cancel</Trans>
          </Button>
          <Button variant="primary" loading={saving} onClick={handleSave}>
            <Trans>Save</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
